/**
 * BB-077 Phase 1.5 · Phone Capture Modal (Desktop Side)
 *
 * Shows a QR code that the user scans with their phone.
 * Polls the backend every 2 seconds for incoming photos.
 * When the phone user taps "Done", imports all photos into the
 * existing bulk-upload AI categorization pipeline.
 *
 * Props:
 *   projectId  — current project ID
 *   onClose    — close modal callback
 *   onImport   — called with { sessionId, fileCount } when photos are
 *                imported into a bulk-upload session (parent should open
 *                BulkUploadWizard in review mode)
 */

import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Camera, X, Loader2, CheckCircle2, Smartphone,
  AlertCircle, ArrowRight, RefreshCw,
} from "lucide-react";
import { Button } from "./ui/button";
import { toast } from "sonner";
import api, { BACKEND_URL } from "@/lib/api";

// ─── Tiny QR code renderer (no external dep) ──────────────────────────────
// We generate the QR via a public API and render as an <img>.
// Uses the goqr.me API which is free and reliable.
function QRCode({ value, size = 220 }) {
  const url = `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodeURIComponent(value)}&margin=8&format=svg`;
  return (
    <img
      src={url}
      alt="QR Code"
      width={size}
      height={size}
      className="rounded-xl border border-slate-200"
    />
  );
}

// ─── Statuses ──────────────────────────────────────────────────────────────
const STATUS_LABELS = {
  open:      "Waiting for phone to connect...",
  capturing: "Phone is capturing photos...",
  done:      "Capture complete!",
  imported:  "Photos imported",
};

// ─── Main Component ────────────────────────────────────────────────────────

export default function PhoneCaptureModal({ projectId, onClose, onImport }) {
  const [phase, setPhase] = useState("creating");  // creating | qr | importing | done | error
  const [captureCode, setCaptureCode] = useState(null);
  const [captureUrl, setCaptureUrl] = useState(null);
  const [expiresAt, setExpiresAt] = useState(null);
  const [sessionStatus, setSessionStatus] = useState("open");
  const [photos, setPhotos] = useState([]);
  const [photoCount, setPhotoCount] = useState(0);
  const [error, setError] = useState(null);
  const [importing, setImporting] = useState(false);
  const pollRef = useRef(null);
  const mountedRef = useRef(true);

  // ── Create capture session on mount ─────────────────────────────────────
  useEffect(() => {
    mountedRef.current = true;
    (async () => {
      try {
        const res = await api.post(`/projects/${projectId}/capture-session`);
        if (!mountedRef.current) return;
        setCaptureCode(res.data.code);
        setCaptureUrl(res.data.capture_url);
        setExpiresAt(res.data.expires_at);
        setPhase("qr");
      } catch (err) {
        if (!mountedRef.current) return;
        const status = err.response?.status;
        const detail = err.response?.data?.detail;
        console.error("Capture session error:", { status, detail, message: err.message });

        if (status === 402) {
          // Subscription issue — paywall interceptor already fired, just close
          onClose();
          return;
        }
        setError(
          detail ||
          (status ? `Server error (${status})` : `Network error: ${err.message}`)
        );
        setPhase("error");
      }
    })();
    return () => { mountedRef.current = false; };
  }, [projectId]);

  // ── Poll for photos ─────────────────────────────────────────────────────
  useEffect(() => {
    if (phase !== "qr" || !captureCode) return;

    const poll = async () => {
      try {
        const res = await api.get(
          `/projects/${projectId}/capture-session/${captureCode}/poll`
        );
        if (!mountedRef.current) return;
        setSessionStatus(res.data.status);
        setPhotos(res.data.photos || []);
        setPhotoCount(res.data.photo_count || 0);
      } catch (err) {
        // Silently ignore poll errors — session might have expired
        console.warn("Poll error:", err.message);
      }
    };

    // Poll immediately, then every 2 seconds
    poll();
    pollRef.current = setInterval(poll, 2000);

    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [phase, captureCode, projectId]);

  // ── Import photos into bulk upload pipeline ─────────────────────────────
  const handleImport = useCallback(async () => {
    if (!captureCode || photoCount === 0) return;
    setImporting(true);

    try {
      const res = await api.post(
        `/projects/${projectId}/capture-session/${captureCode}/import`
      );
      setPhase("done");
      toast.success(`${res.data.file_count} photos ready for AI categorization`);

      if (onImport) {
        onImport({
          sessionId: res.data.bulk_upload_session_id,
          fileCount: res.data.file_count,
        });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
    }
  }, [captureCode, photoCount, projectId, onImport]);

  // ── Auto-import when phone marks done ───────────────────────────────────
  useEffect(() => {
    if (sessionStatus === "done" && phase === "qr" && photoCount > 0 && !importing) {
      // Small delay so user sees the "Capture complete!" state
      const timer = setTimeout(() => {
        handleImport();
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [sessionStatus, phase, photoCount, importing, handleImport]);

  // ── Copy link to clipboard ──────────────────────────────────────────────
  const copyLink = useCallback(() => {
    if (captureUrl) {
      navigator.clipboard.writeText(captureUrl).then(() => {
        toast.success("Link copied! Open it on your phone.");
      });
    }
  }, [captureUrl]);

  // ── Render ──────────────────────────────────────────────────────────────

  return (
    <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-gradient-to-r from-indigo-50 to-blue-50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-indigo-100 rounded-xl flex items-center justify-center">
              <Smartphone className="h-5 w-5 text-indigo-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-800">Snap from Phone</h2>
              <p className="text-xs text-slate-500">
                {phase === "creating" && "Setting up..."}
                {phase === "qr" && (STATUS_LABELS[sessionStatus] || "Connecting...")}
                {phase === "importing" && "Importing photos..."}
                {phase === "done" && "Photos imported!"}
                {phase === "error" && "Something went wrong"}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-slate-600 rounded-xl hover:bg-white/60 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-6">
          {/* Creating */}
          {phase === "creating" && (
            <div className="text-center py-8">
              <Loader2 className="h-8 w-8 text-blue-500 animate-spin mx-auto mb-3" />
              <p className="text-slate-600">Creating capture session...</p>
            </div>
          )}

          {/* Error */}
          {phase === "error" && (
            <div className="text-center py-8">
              <div className="w-14 h-14 bg-red-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="h-7 w-7 text-red-500" />
              </div>
              <p className="text-slate-800 font-semibold mb-1">Failed to Start</p>
              <p className="text-sm text-slate-500">{error}</p>
            </div>
          )}

          {/* QR Code + photo stream */}
          {phase === "qr" && (
            <div className="space-y-5">
              {/* QR Code */}
              {sessionStatus === "open" && (
                <div className="flex flex-col items-center">
                  {captureUrl && <QRCode value={captureUrl} size={200} />}
                  <p className="text-sm text-slate-600 mt-3 text-center">
                    Scan this QR code with your phone camera
                  </p>
                  <button
                    onClick={copyLink}
                    className="mt-2 text-xs text-blue-500 hover:text-blue-600 underline"
                  >
                    Or copy the link
                  </button>
                  {captureCode && (
                    <div className="mt-3 bg-slate-100 rounded-xl px-4 py-2 text-center">
                      <p className="text-xs text-slate-400 mb-0.5">Session code</p>
                      <p className="text-2xl font-mono font-bold text-slate-800 tracking-widest">
                        {captureCode}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Photo stream — shows when photos start arriving */}
              {(sessionStatus === "capturing" || sessionStatus === "done") && (
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    {sessionStatus === "capturing" ? (
                      <div className="flex items-center gap-2 text-blue-600">
                        <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
                        <span className="text-sm font-medium">
                          Receiving photos... ({photoCount})
                        </span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 text-green-600">
                        <CheckCircle2 className="h-4 w-4" />
                        <span className="text-sm font-medium">
                          {photoCount} photo{photoCount !== 1 ? "s" : ""} captured
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Thumbnails */}
                  <div className="grid grid-cols-4 gap-2 max-h-48 overflow-y-auto">
                    {photos.map((photo, i) => (
                      <div
                        key={photo.file_id}
                        className="aspect-square rounded-lg overflow-hidden bg-slate-100 relative"
                      >
                        <img
                          src={`${BACKEND_URL}${photo.url}`}
                          alt={photo.filename}
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute bottom-0.5 right-0.5 bg-black/60 text-white text-[10px] px-1 py-0.5 rounded">
                          {i + 1}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* QR still visible but smaller */}
                  {sessionStatus === "capturing" && captureUrl && (
                    <details className="text-center">
                      <summary className="text-xs text-slate-400 cursor-pointer hover:text-slate-600">
                        Show QR code again
                      </summary>
                      <div className="mt-2 flex justify-center">
                        <QRCode value={captureUrl} size={120} />
                      </div>
                    </details>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Done */}
          {phase === "done" && (
            <div className="text-center py-6">
              <div className="w-14 h-14 bg-green-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="h-7 w-7 text-green-600" />
              </div>
              <p className="text-slate-800 font-semibold mb-1">Photos Imported!</p>
              <p className="text-sm text-slate-500">
                {photoCount} photo{photoCount !== 1 ? "s" : ""} are ready for
                AI categorization in the Bulk Upload wizard.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-slate-200 bg-slate-50">
          {phase === "qr" && photoCount > 0 && sessionStatus !== "done" && (
            <Button
              onClick={handleImport}
              disabled={importing}
              className="bg-blue-600 hover:bg-blue-500 text-white gap-2 ml-auto"
            >
              {importing ? (
                <><Loader2 className="h-4 w-4 animate-spin" /> Importing...</>
              ) : (
                <><ArrowRight className="h-4 w-4" /> Import {photoCount} Photos</>
              )}
            </Button>
          )}
          {phase === "qr" && photoCount === 0 && (
            <div className="ml-auto">
              <Button variant="outline" onClick={onClose}>Cancel</Button>
            </div>
          )}
          {phase === "done" && (
            <div className="ml-auto">
              <Button onClick={onClose} className="bg-blue-600 hover:bg-blue-500 text-white gap-2">
                <CheckCircle2 className="h-4 w-4" /> Done
              </Button>
            </div>
          )}
          {(phase === "creating" || phase === "error") && (
            <div className="ml-auto">
              <Button variant="outline" onClick={onClose}>Close</Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
